import mapboxgl from 'mapbox-gl';
import ZoomControl from 'mapbox-gl-controls/lib/zoom';
import CompassControl from 'mapbox-gl-controls/lib/compass';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'mapbox-gl-controls/theme.css';
import { stringify } from 'qs';
import { LAYERS, LAYERS_TO_COLORIZE, PALETTE, getColor, initMapLayers } from '../../Map/layers';
import ROOMS from './rooms';
import { attachMapAnalytics, sendEvent } from '../../helpers/analytics';
import { getBuildingsDataByIds } from '../../helpers/getBuildingsDataByIds';

const LAYOUTS_LAYER = 'layouts';
const WALLS = ['wall', 'door', 'window'];
const ROOMS_FLOOR = Object.keys(ROOMS).filter((type) => !WALLS.includes(type));

function toFeatureCollection(features) {
  const result = [];
  features.forEach((feature) => {
    const level = feature.properties.level ?? 1;
    const type = feature.properties.type;
    if (level > 1 || type === 'step') return;
    ROOMS[type].forEach((properties) => {
      result.push({
        ...feature,
        properties: {
          ...feature.properties,
          ...properties,
          ...(!WALLS.includes(type) && { color: '#19195F' }),
        },
      });
    });
  });

  return {
    type: 'FeatureCollection',
    features: result,
  };
}

class PlanMap {
  constructor(params = {}, options = {}) {
    this.data = null;
    this.floor = null;
    this.params = params;
    this.options = options;
    this.colorizeWalls = ['literal', WALLS];
    this.colorizeRoomsFloor = ['literal', ROOMS_FLOOR];
    this.buildingData = [];
    this.Z_FIGHTING = 0.05;
    this.BOUNDS_PADDING = 60;

    this.root = document.getElementById('map');
    if (!this.root) return;
    this.fetchBuildingData();
    this.getData();
  }

  getData() {
    const apiUrl = `/api/floor-layouts?${stringify(
      {
        layoutId: this.params.layoutId,
        language: window.currentLanguage,
        floorNumber: this.floor || this.params.floorNumber,
      },
      { skipNulls: true },
    )}`;
    fetch(apiUrl)
      .then((res) => {
        if (!res.ok) return;
        res.json().then((json) => {
          this.data = json;
          if (this.data.length === 0) {
            this.root.classList.add('hidden');
            return;
          }
          this.root.classList.remove('hidden');
          this.floor = Object.keys(json).reverse()[0];
          this.render();
          this.hover();
        });
      })
      .catch((error) => {
        console.warn(error);
      });
  }

  getBounds(floorBounds) {
    const bounds = new mapboxgl.LngLatBounds();
    this.data[this.floor].forEach((feature) => {
      if (!floorBounds && feature.properties.layout !== this.params.layoutId) return;
      const coordinates = feature.geometry.coordinates[0];

      coordinates.forEach((c) => bounds.extend(c));
    });
    return bounds;
  }

  fetchBuildingData() {
    getBuildingsDataByIds([this.params.buildingId], (buildingData) => {
      this.buildingData = buildingData;
    });
  }

  initSource() {
    const data = toFeatureCollection(this.data[this.floor]);
    const source = this.map.getSource('layouts');

    if (source) {
      source.setData(data);
    } else {
      this.map.addSource('layouts', {
        type: 'geojson',
        data,
      });
    }
  }

  render() {
    if (!this.map) {
      const container = document.getElementById('map-container');

      this.map = new mapboxgl.Map({
        container,
        style: window.mapStreetsStyle,
        bounds: this.getBounds(),
        fitBoundsOptions: { padding: this.BOUNDS_PADDING },
        scrollZoom: false,
        minZoom: 13,
        dragPan: !window.isMobile,
        pitch: 25,
        attributionControl: false,
      });

      this.map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
      this.map.addControl(new ZoomControl(), 'bottom-right');
      this.map.addControl(new CompassControl(), 'bottom-right');
      attachMapAnalytics(container);
    } else {
      this.initSource();
      this.highlightLayout();
      this.onLoad();
    }

    this.map.on('style.load', () => {
      this.map.setPaintProperty(LAYERS.sections, 'fill-extrusion-height', 0);
      initMapLayers(this.map);
      this.colorize();
    });

    this.map.on('load', () => {
      this.initSource();

      this.map.addLayer({
        id: LAYOUTS_LAYER,
        type: 'fill-extrusion',
        source: 'layouts',
      });
      this.highlightLayout();
      this.onLoad();

      this.map.on('click', LAYOUTS_LAYER, (event) => {
        const feature = event.features[0].properties;
        const zoomLevel = this.map.getZoom();
        if (feature.layout === this.params.layoutId) return;
        if (!this.options.updateModal) {
          window.location.href = feature.url;
        } else if (feature.flat !== 'null') {
          // mapbox-gl-js bug: features with value null converting to "null"
          // https://github.com/mapbox/mapbox-gl-js/issues/8497
          // fixed on v2.6.0 https://github.com/mapbox/mapbox-gl-js/releases/tag/v2.6.0
          this.options.updateModal(Number(feature.flat), { layout_id: Number(feature.layout) });
        } else {
          this.options.updateModal(Number(feature.layout));
        }
        sendEvent(window.eventCategory, `map_zoom_${zoomLevel}`, 'layout_click');
      });
    });
  }

  colorize() {
    LAYERS_TO_COLORIZE.forEach((layerId) => {
      const layer = this.map.getLayer(layerId);
      const colorProperty = `${layer.type}-color`;

      this.map.setPaintProperty(
        layerId,
        colorProperty,
        getColor(this.params.buildingId, {
          selectedSectionIds: [this.params.sectionId],
          filtered: this.buildingData,
          layerId,
        }),
      );
    });
  }

  onLoad() {
    this.initFitBounds(true);
  }

  unmount() {
    if (this.options.unmountPlanVrControls) this.options.unmountPlanVrControls();
  }

  highlightLayout(id = 0) {
    this.map.setPaintProperty(LAYOUTS_LAYER, 'fill-extrusion-color', [
      'case',
      [
        'all',
        ['==', ['get', 'layout'], this.params.layoutId],
        ['in', ['get', 'type'], this.colorizeWalls],
      ],
      PALETTE.primary,
      [
        'all',
        ['==', ['get', 'layout'], this.params.layoutId],
        ['in', ['get', 'type'], this.colorizeRoomsFloor],
      ],
      PALETTE.light,
      ['all', ['==', ['get', 'layout'], id], ['in', ['get', 'type'], this.colorizeWalls]],
      PALETTE.light,
      ['get', 'color'],
    ]);
    // increase a bit wall height to prevent z-fighting
    this.map.setPaintProperty(LAYOUTS_LAYER, 'fill-extrusion-base', ['get', 'base']);
    this.map.setPaintProperty(LAYOUTS_LAYER, 'fill-extrusion-height', [
      'case',
      [
        'all',
        ['==', ['get', 'layout'], this.params.layoutId],
        ['in', ['get', 'type'], this.colorizeWalls],
      ],
      ['+', ['get', 'height'], this.Z_FIGHTING + 0.02],
      ['all', ['==', ['get', 'layout'], id], ['in', ['get', 'type'], this.colorizeWalls]],
      ['+', ['get', 'height'], this.Z_FIGHTING + 0.01],
      ['+', ['get', 'height'], this.Z_FIGHTING],
    ]);
  }

  hover() {
    this.map.on('mousemove', LAYOUTS_LAYER, (e) => {
      this.map.getCanvas().style.cursor = 'pointer';
      const feature = e.features[0];
      this.highlightLayout(feature.properties.layout);
    });
    this.map.on('mouseleave', LAYOUTS_LAYER, () => {
      this.map.getCanvas().style.cursor = '';
      this.highlightLayout();
    });
  }

  initFitBounds(floorBounds) {
    this.map?.fitBounds(this.getBounds(floorBounds), {
      padding: this.BOUNDS_PADDING,
    });
  }

  updateFloor(floorNumber) {
    this.floor = floorNumber;
    this.getData();
  }
}

export default PlanMap;
